import React, { useEffect, useState, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { FingerConfig, FingerType, TrainingMode, HandMode, ComplexityMode } from '@/types';
// 🎯 PHASE 1: Hand Picker Integration
import LeftHandPicker from '@/components/interactive-left-hand-picker/components/LeftHandPicker';
import RightHandPicker from '@/components/interactive-left-hand-picker/components/RightHandPicker';
import '@/components/interactive-left-hand-picker/index.css'; // Soaring animation styles
import { FINGER_KEY_MAP } from '@/components/interactive-left-hand-picker/constants';
import { RIGHT_FINGER_KEY_MAP } from '@/components/interactive-left-hand-picker/rightHandConstants';
import { getFingerDisplayName, LeftHandFinger, RightHandFinger } from '@/components/interactive-left-hand-picker/types';
import { useGameEngine } from '@/hooks/useGameEngine';
import { useKeyboardInput } from '@/hooks/useKeyboardInput';
import { getFingerForKey, getAllActiveFingers, getKeysForFingers } from '@/data/fingerKeyMapping';
import { getEffectiveTrainingConfiguration } from '@/services/courseIntegrationService';
import { getCourseRound } from '@/data/courseStructure';
import { TrainingMode as UtilTrainingMode } from '@/utils/trainingGenerator';
import LetterDisplay from './LetterDisplay';
import ProgressVisualization from './ProgressVisualization';
import FingerHints from './FingerHints';
import FeedbackSystem from './FeedbackSystem';
import UnifiedCourseWidget from './UnifiedCourseWidget';
import TrainingTimer from './TrainingTimer';
import OneWordMetricsHUD from './OneWordMetricsHUD';
import OneWordRaceComplete from './OneWordRaceComplete';
import OneWordTrainingStats from './OneWordTrainingStats';
import { getOneWordRecord, OneWordRecord } from '@/services/oneWordRecordsService';
import { saveOneWordRace } from '@/services/oneWordRacesService';
interface GameScreenProps {
  // 🔧 SPRINT 2 TASK 2.1: Simplified props interface - unidirectional data flow
  trainingMode: TrainingMode;
  showFingerHints?: boolean;
  sessionStatus?: 'ready' | 'active' | 'completed';
  // 🔧 REMOVED: isSessionActiveFromApp (bidirectional sync)
  // 🔧 REMOVED: onSessionActiveChange (bidirectional sync)
  onSessionStatusChange?: (status: 'ready' | 'active' | 'completed') => void;

  // Legacy props for compatibility
  levelNumber?: number;
  dayNumber?: number;
  handMode?: HandMode;
  complexityMode?: ComplexityMode;

  // Custom mode props
  customFingers?: FingerType[];

  // Course mode props
  roundNumber?: number;
  mode?: 'course';

  // One Word Training props
  uid?: string;

  // SPRINT 2: Course progress and callbacks
  courseProgress?: any; // CourseProgress object
  onCourseDaySelect?: (levelNumber: number, dayNumber: number) => void;
  onCourseRoundSelect?: (levelNumber: number, roundNumber: number) => void;

  // Callbacks
  onSessionComplete?: (stats: any) => void;
  onBackToMenu?: () => void;
  // 🎯 PHASE 2: Hand picker change callback
  onFingerSelectionChange?: (fingers: FingerType[]) => void;
  onModeChange?: (mode: TrainingMode) => void;
  onComplexityChange?: (mode: ComplexityMode) => void;
  onRequestLogin?: () => void;
  isAuthenticated?: boolean;
  className?: string;
}

/**
 * SPRINT 1: Enhanced finger mapping for custom mode
 * Maps selected finger types to both hands based on hand mode
 */
const mapCustomFingersToHands = (selectedFingers: FingerType[], handMode: HandMode): {
  activeFingersLeft: FingerType[];
  activeFingersRight: FingerType[];
} => {
  console.log('🔧 mapCustomFingersToHands called with:', { selectedFingers, handMode });

  if (handMode === 'both') {
    return {
      activeFingersLeft: selectedFingers.filter(f => f.startsWith('left')),
      activeFingersRight: selectedFingers.filter(f => f.startsWith('right'))
    };
  }

  if (handMode === 'left') {
    return {
      activeFingersLeft: selectedFingers.filter(f => f.startsWith('left')),
      activeFingersRight: []
    };
  }

  // handMode === 'right'
  return {
    activeFingersLeft: [],
    activeFingersRight: selectedFingers.filter(f => f.startsWith('right'))
  };
};

export const GameScreen = React.forwardRef<any, GameScreenProps>(({
  trainingMode,
  showFingerHints = false,
  sessionStatus = 'ready',
  // 🔧 SPRINT 2 TASK 2.1: Removed bidirectional sync props
  // REMOVED: isSessionActiveFromApp = false,
  onSessionStatusChange,
  // REMOVED: onSessionActiveChange,
  levelNumber,
  dayNumber,
  handMode,
  complexityMode,
  customFingers,
  roundNumber,
  uid,
  courseProgress,
  onCourseDaySelect,
  onCourseRoundSelect,
  onSessionComplete,
  onFingerSelectionChange,
  onModeChange,
  onComplexityChange,
  onRequestLogin,
  isAuthenticated,
  className = ''
}, ref) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [oneWordText, setOneWordText] = useState('');

  // One Word session-local lap metrics
  const [oneWordRecord, setOneWordRecord] = useState<OneWordRecord | null>(null);
  const [oneWordLap, setOneWordLap] = useState<{
    bestLapMs: number;
    currentStreak: number;
    bestStreak: number;
    avgLapMs: number;
    lapsCompleted: number;
    lapsClean: number;
    totalTimeMs: number;
  }>({ bestLapMs: 0, currentStreak: 0, bestStreak: 0, avgLapMs: 0, lapsCompleted: 0, lapsClean: 0, totalTimeMs: 0 });
  const oneWordLapStartRef = useRef<number>(0);
  const oneWordLastCorrectRef = useRef<number>(0);
  const oneWordLastWrongRef = useRef<number>(0);
  const oneWordLapHadErrorRef = useRef<boolean>(false);
  // 🚨 SPRINT 2 TASK 2.2: Add completion tracking ref to prevent duplicate completion calls
  const sessionCompletedRef = useRef(false);
  const lastCompletedSessionIdRef = useRef<string | null>(null);
  // Separate guard for the One Word race-persist effect so it does not interfere
  // with the completion-detection effect's use of lastCompletedSessionIdRef.
  const oneWordRaceSavedSessionIdRef = useRef<string | null>(null);

  // Derive fingerConfig from the typed One Word — letters in word determine active fingers
  const oneWordFingerConfig = useMemo<FingerConfig | null>(() => {
    if (trainingMode !== 'oneWord' || !oneWordText.trim()) return null;
    const left = new Set<FingerType>();
    const right = new Set<FingerType>();
    for (const ch of oneWordText.toUpperCase()) {
      const key = ch === ' ' ? 'SPACE' : ch;
      const f = getFingerForKey(key);
      if (!f) continue;
      if (f.startsWith('left')) left.add(f); else right.add(f);
    }
    return {
      activeFingersLeft: Array.from(left),
      activeFingersRight: Array.from(right),
      letterComplexity: { min: 1, max: Math.max(2, oneWordText.trim().length) },
    };
  }, [trainingMode, oneWordText]);
  // 🐛 DEBUG: Add logging for session status (removed bidirectional sync refs)
  console.log('🎯 GameScreen Debug:', {
    sessionStatus,
    // REMOVED: isSessionActiveFromApp,
    trainingMode,
    levelNumber,
    dayNumber,
    roundNumber
  });

  const {
    gameState,
    startSession,
    endSession,
    processKeystroke,
    pauseSession,
    resumeSession,
    isSessionActive,
    currentSequence,
    currentSequenceIndex,
    nextSequence,
    stats,
    letterMistakes,
    timeRemaining,
    progressTiles,
    originalSessionContext
  } = useGameEngine();

  // One Word — load best record for the typed word
  useEffect(() => {
    if (trainingMode !== 'oneWord' || !uid || !oneWordText.trim()) {
      setOneWordRecord(null);
      return;
    }
    let cancelled = false;
    getOneWordRecord(uid, oneWordText).then((r) => {
      if (!cancelled) setOneWordRecord(r);
    });
    return () => {
      cancelled = true;
    };
  }, [trainingMode, uid, oneWordText]);

  // One Word — reset lap metrics when session starts or word changes
  useEffect(() => {
    if (trainingMode !== 'oneWord') return;
    setOneWordLap({
      bestLapMs: 0,
      currentStreak: 0,
      bestStreak: 0,
      avgLapMs: 0,
      lapsCompleted: 0,
      lapsClean: 0,
      totalTimeMs: 0,
    });
    oneWordLapStartRef.current = 0;
    oneWordLastCorrectRef.current = 0;
    oneWordLastWrongRef.current = 0;
    oneWordLapHadErrorRef.current = false;
  }, [trainingMode, oneWordText, gameState.session?.startTime]);

  // One Word — lap detection: each completed sequence = one lap (manual selection emits one sequence per lap)
  useEffect(() => {
    if (trainingMode !== 'oneWord' || !gameState.isActive || !gameState.session) return;

    const lapLength = currentSequence?.length || 0;
    if (lapLength <= 0) return;

    const correct = stats.correctLetters;
    const wrong = stats.wrongAttempts;
    const lastCorrect = oneWordLastCorrectRef.current;
    const lastWrong = oneWordLastWrongRef.current;
    const now = Date.now();

    if (oneWordLapStartRef.current === 0 && correct > lastCorrect) {
      oneWordLapStartRef.current = now;
      oneWordLapHadErrorRef.current = false;
    }

    if (wrong > lastWrong) {
      oneWordLapHadErrorRef.current = true;
    }

    const lapsBefore = Math.floor(lastCorrect / lapLength);
    const lapsNow = Math.floor(correct / lapLength);

    if (lapsNow > lapsBefore && oneWordLapStartRef.current > 0) {
      const lapMs = now - oneWordLapStartRef.current;
      const clean = !oneWordLapHadErrorRef.current;

      setOneWordLap((prev) => {
        const lapsCompleted = prev.lapsCompleted + 1;
        const lapsClean = prev.lapsClean + (clean ? 1 : 0);
        const totalTimeMs = prev.totalTimeMs + lapMs;
        const newCurrentStreak = clean ? prev.currentStreak + 1 : 0;
        // Best Lap = fastest *clean* finish; only clean laps update it.
        const bestLapMs = clean
          ? (prev.bestLapMs === 0 ? lapMs : Math.min(prev.bestLapMs, lapMs))
          : prev.bestLapMs;
        return {
          bestLapMs,
          currentStreak: newCurrentStreak,
          bestStreak: Math.max(prev.bestStreak, newCurrentStreak),
          avgLapMs: Math.round(totalTimeMs / lapsCompleted),
          lapsCompleted,
          lapsClean,
          totalTimeMs,
        };
      });

      oneWordLapStartRef.current = now;
      oneWordLapHadErrorRef.current = false;
    }

    oneWordLastCorrectRef.current = correct;
    oneWordLastWrongRef.current = wrong;
  }, [trainingMode, gameState.isActive, gameState.session, stats.correctLetters, stats.wrongAttempts, currentSequence]);

  // One Word — persist one race (time-series doc + aggregate record) on session end.
  // Guarded so a finished session is saved exactly once even if deps re-fire.
  useEffect(() => {
    if (trainingMode !== 'oneWord') return;
    if (gameState.isActive) return;
    if (!uid || !oneWordText.trim()) return;
    if (oneWordLap.lapsCompleted === 0) return;

    const sessionId = gameState.session?.id || null;
    if (!sessionId || oneWordRaceSavedSessionIdRef.current === sessionId) return;
    oneWordRaceSavedSessionIdRef.current = sessionId;

    const startedAt = gameState.session?.startTime || Date.now();
    const completedAt = gameState.session?.endTime || Date.now();

    saveOneWordRace(uid, oneWordText, {
      startedAt,
      completedAt,
      bestLapMs: oneWordLap.bestLapMs,
      bestStreak: oneWordLap.bestStreak,
      lapsCompleted: oneWordLap.lapsCompleted,
      lapsClean: oneWordLap.lapsClean,
      avgLapMs: oneWordLap.avgLapMs,
      totalTimeMs: oneWordLap.totalTimeMs,
      letterMistakes: letterMistakes || {},
    }).then((updated) => {
      if (updated) setOneWordRecord(updated);
    });
  }, [trainingMode, gameState.isActive, gameState.session, uid, oneWordText, oneWordLap, letterMistakes]);

  // 📚 Course mode configuration state
  const [courseConfig, setCourseConfig] = useState<{
    fingerConfig: FingerConfig;
    utilTrainingMode: UtilTrainingMode;
    description: string;
    isCustom: boolean;
  } | null>(null);

  /**
   * Load course configuration from Firebase (for course mode)
   */
  useEffect(() => {
    if (trainingMode === 'course' && dayNumber && roundNumber) {
      const currentLevel = levelNumber || 1;

      getEffectiveTrainingConfiguration(currentLevel, dayNumber, roundNumber)
        .then(config => {
          console.log('🔥 Firebase course config loaded:', {
            level: currentLevel,
            day: dayNumber,
            round: roundNumber,
            isCustom: config.isCustom,
            fingerConfig: config.fingerConfig,
            trainingMode: config.trainingMode
          });

          const utilTrainingMode: UtilTrainingMode = {
            hands: config.trainingMode?.hands || 'both',
            complexity: config.trainingMode?.complexity || 1
          };

          setCourseConfig({
            fingerConfig: config.fingerConfig,
            utilTrainingMode,
            description: config.isCustom
              ? `Custom: Day ${dayNumber}, Round ${roundNumber} (Firebase)`
              : `Day ${dayNumber}, Round ${roundNumber} (Default)`,
            isCustom: config.isCustom
          });
        })
        .catch(error => {
          console.warn('⚠️ Firebase course config not available, using default structure:', error);

          // Always fallback to hardcoded structure for now
          const fallbackRound = getCourseRound(currentLevel, dayNumber, roundNumber);
          if (fallbackRound) {
            const utilTrainingMode: UtilTrainingMode = {
              hands: 'both',
              complexity: fallbackRound.fingerConfig.letterComplexity.max as any
            };

            setCourseConfig({
              fingerConfig: fallbackRound.fingerConfig,
              utilTrainingMode,
              description: fallbackRound.description,
              isCustom: false
            });
          } else {
            // Ultimate fallback to level 1 day 1 round 1
            const ultimateFallback = getCourseRound(1, 1, 1);
            if (ultimateFallback) {
              const utilTrainingMode: UtilTrainingMode = {
                hands: 'both',
                complexity: ultimateFallback.fingerConfig.letterComplexity.max as any
              };

              setCourseConfig({
                fingerConfig: ultimateFallback.fingerConfig,
                utilTrainingMode,
                description: ultimateFallback.description,
                isCustom: false
              });
            }
          }
        })
        .finally(() => {
          // Loading state removed - no longer needed
        });
    }
  }, [trainingMode, levelNumber, dayNumber, roundNumber]);

  /**
   * SPRINT 1: Get training configuration based on mode (MEMOIZED)
   */
  const { fingerConfig, utilTrainingMode, description } = useMemo(() => {
    if (trainingMode === 'oneWord') {
      const derived: FingerConfig = oneWordFingerConfig ?? {
        activeFingersLeft: [],
        activeFingersRight: [],
        letterComplexity: { min: 1, max: 2 },
      };
      return {
        fingerConfig: derived,
        utilTrainingMode: { hands: 'both', complexity: 'words' } as UtilTrainingMode,
        description: oneWordText.trim() ? `One Word: "${oneWordText.trim()}"` : 'One Word Training',
      };
    }

    if (trainingMode === 'custom') {
      console.log('🎨 Custom mode configuration');

      // Custom mode: use provided fingers, hand mode, and complexity
      const fingers = customFingers || ['leftPinky', 'rightPinky'];
      const hands = handMode || 'both';
      const complexity = complexityMode || 1;

      const handFiltered = mapCustomFingersToHands(fingers, hands);
      const complexityMax = typeof complexity === 'number' ? complexity : complexity === 'words' ? 5 : 1;

      const fingerConfig: FingerConfig = {
        ...handFiltered,
        letterComplexity: {
          min: 1,
          max: Math.max(1, complexityMax)
        }
      };

      const utilTrainingMode: UtilTrainingMode = {
        hands,
        complexity
      };

      const activeFingerNames = [...fingerConfig.activeFingersLeft, ...fingerConfig.activeFingersRight]
        .map(f => f.replace('left', '').replace('right', ''))
        .filter((v, i, a) => a.indexOf(v) === i)
        .join(', ');

      return {
        fingerConfig,
        utilTrainingMode,
        description: `Custom: ${activeFingerNames} (${hands} hand(s), 1-${complexityMax} letters)`
      };

    } else if (courseConfig) {
      console.log('📚 Course mode configuration from Firebase:', {
        isCustom: courseConfig.isCustom,
        description: courseConfig.description
      });

      return {
        fingerConfig: courseConfig.fingerConfig,
        utilTrainingMode: courseConfig.utilTrainingMode,
        description: courseConfig.description
      };

    } else {
      console.log('📚 Course mode configuration - loading or fallback');

      // Course mode: get preset configuration from course structure (fallback)
      const currentLevel = levelNumber || 1;
      const currentDay = dayNumber || 1;
      const currentRound = roundNumber || 1;

      const courseRound = getCourseRound(currentLevel, currentDay, currentRound);

      if (!courseRound) {
        console.error('❌ Course round not found:', { currentLevel, currentDay, currentRound });
        // Fallback to day 1 round 1
        const fallbackRound = getCourseRound(1, 1, 1);
        if (!fallbackRound) {
          throw new Error('Course structure is corrupted - cannot find Day 1 Round 1');
        }
        return {
          fingerConfig: fallbackRound.fingerConfig,
          utilTrainingMode: { hands: 'both', complexity: 1 },
          description: fallbackRound.description
        };
      }

      const utilTrainingMode: UtilTrainingMode = {
        hands: 'both', // Course mode always uses both hands in the preset config
        complexity: courseRound.fingerConfig.letterComplexity.max as any
      };

      return {
        fingerConfig: courseRound.fingerConfig,
        utilTrainingMode,
        description: courseRound.description
      };
    }
  }, [trainingMode, customFingers, handMode, complexityMode, levelNumber, dayNumber, roundNumber, courseConfig, oneWordFingerConfig, oneWordText]);

  console.log('🎯 Final training configuration:', {
    mode: trainingMode,
    fingerConfig,
    utilTrainingMode,
    description
  });

  /**
   * Handle keystroke input
   */
  const handleKeyPress = useCallback((key: string) => {
    if (!isSessionActive || isCalibrating) return;

    // Process the keystroke through the game engine
    processKeystroke(key);
  }, [isSessionActive, isCalibrating, processKeystroke]);

  /**
   * Keyboard input configuration
   */
  const { focusKeyboardCapture } = useKeyboardInput({
    onKeyPress: handleKeyPress,
    enabled: isSessionActive && !isCalibrating,
    preventDefaultKeys: ['all'], // Prevent all default behaviors during training
    ignoreKeys: ['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Tab', 'Escape']
  });

  /**
   * Add error to app state (placeholder - this should be passed down from App)
   */
  const addError = (type: string, message: string) => {
    console.error(`[${type}] ${message}`);
    // In a real implementation, this would bubble up to App component
  };

  /**
   * SPRINT 1: Enhanced start session with mode-specific validation
   * 🔧 MOVED BEFORE useImperativeHandle to fix initialization error
   */
  const handleStartSession = useCallback(async () => {
    console.log('🚀 Starting new session with configuration:');
    console.log('  Mode:', trainingMode);
    console.log('  Finger config:', fingerConfig);
    console.log('  Util training mode:', utilTrainingMode);

    // Validate configuration before starting
    const activeFingers = getAllActiveFingers(fingerConfig);
    if (activeFingers.length === 0) {
      console.error('❌ Cannot start session: No active fingers!');
      addError('game', 'No active fingers configured for this training setup');
      return;
    }

    const availableKeys = getKeysForFingers(activeFingers);
    if (availableKeys.length === 0) {
      console.error('❌ Cannot start session: No available keys!');
      addError('game', 'No keys available for selected fingers');
      return;
    }

    console.log('✅ Configuration valid, starting session...');
    console.log('  Active fingers:', activeFingers);
    console.log('  Available keys:', availableKeys);
    console.log('  Complexity range:', fingerConfig.letterComplexity);

    // Use appropriate day number for session tracking
    const sessionDayNumber = trainingMode === 'course' ? (dayNumber || 1) : 1;
    const sessionRoundNumber = trainingMode === 'course' ? (roundNumber || 1) : 1;

    try {
      const systemMode = trainingMode === 'course' ? 'course' : 'custom';
      const manualOverride =
        trainingMode === 'oneWord' && oneWordText.trim()
          ? {
              selectedWords: [oneWordText.trim()],
              selectedPhrases: [],
              randomizeOrder: false,
            }
          : undefined;

      await startSession(
        fingerConfig,
        sessionDayNumber,
        utilTrainingMode as any,
        sessionRoundNumber,
        systemMode,
        trainingMode === 'course' ? (levelNumber || 1) : undefined,
        manualOverride
      );
      onSessionStatusChange?.('active');
      focusKeyboardCapture();
    } catch (error) {
      // 🔥 SIMPLIFIED: No more consonant detection - just log and show general error
      console.error('❌ Error starting session:', error);
      addError('game', 'Failed to start training session');
    }
  }, [fingerConfig, utilTrainingMode, dayNumber, roundNumber, levelNumber, trainingMode, startSession, onSessionStatusChange, focusKeyboardCapture, oneWordText]);

  // 🔧 CRITICAL FIX: Expose control functions via ref (AFTER useGameEngine)
  useImperativeHandle(ref, () => ({
    handlePauseResume: () => {
      console.log('🎯 GameScreen handlePauseResume called', { isSessionActive });
      if (isSessionActive) {
        pauseSession();
        console.log('⏸️ Pausing session');
      } else if (sessionStatus === 'active') {
        resumeSession();
        console.log('▶️ Resuming session');
      }
    },
    handleEndSession: () => {
      console.log('🛑 GameScreen handleEndSession called');
      endSession();
      onSessionStatusChange?.('ready');
      console.log('⏹️ Session stopped');
    },
    handleStartSession: () => {
      console.log('🚀 GameScreen handleStartSession called from header');
      handleStartSession();
    }
  }), [isSessionActive, sessionStatus, pauseSession, resumeSession, endSession, onSessionStatusChange, handleStartSession]);

  // 🐛 DEBUG: Add logging for progress tiles
  console.log('📊 Progress Tiles Debug:', {
    progressTilesLength: progressTiles?.length || 0,
    sessionStatus,
    shouldShowProgress: sessionStatus === 'active',
    isSessionActive
  });

  /**
   * UPDATED: Get current and next finger for hints from sequence
   */
  const currentFinger = currentSequence && currentSequence[currentSequenceIndex] ? getFingerForKey(
    currentSequence[currentSequenceIndex].char === '_' ? 'SPACE' : currentSequence[currentSequenceIndex].char
  ) : null;

  const nextFinger = (() => {
    // Next finger can be:
    // 1. Next letter in current sequence
    if (currentSequence && currentSequenceIndex + 1 < currentSequence.length) {
      const nextLetterInSequence = currentSequence[currentSequenceIndex + 1];
      return getFingerForKey(nextLetterInSequence.char === '_' ? 'SPACE' : nextLetterInSequence.char);
    }
    // 2. First letter of next sequence
    if (nextSequence && nextSequence.length > 0) {
      return getFingerForKey(nextSequence[0].char === '_' ? 'SPACE' : nextSequence[0].char);
    }
    return null;
  })();

  /**
   * Initialize session on mount
   */
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      focusKeyboardCapture();
    }
  }, [isInitialized, focusKeyboardCapture]);

  /**
   * 🔧 SPRINT 2 TASK 2.2 & 2.3: UNIDIRECTIONAL session management effect - NO MORE BIDIRECTIONAL SYNC
   */
  useEffect(() => {
    // 🔧 TASK 2.2: Handle session completion (only once per session)
    if (gameState.session && !gameState.isActive && gameState.session.endTime) {
      const sessionId = gameState.session.id;

      // Prevent duplicate completion calls for the same session
      if (!sessionCompletedRef.current || lastCompletedSessionIdRef.current !== sessionId) {
        console.log('✅ Session completion detected for session:', sessionId);

        // Mark this session as completed
        sessionCompletedRef.current = true;
        lastCompletedSessionIdRef.current = sessionId;

        // 🔧 TASK 2.2: Only report completion to App - no bidirectional sync
        onSessionStatusChange?.('completed');

        // Call completion handler with context
        if (originalSessionContext) {
          console.log('✅ Session completed with original context:', originalSessionContext);
          onSessionComplete?.({
            stats,
            originalContext: originalSessionContext
          });
        } else {
          console.warn('⚠️ Session completed but no original context available');
          onSessionComplete?.(stats);
        }
      }
    }

    // 🔧 TASK 2.2: Handle session status changes - App is source of truth
    if (isSessionActive && sessionStatus !== 'active') {
      // GameScreen session is active but App thinks it's not - report to App
      console.log('📡 Reporting session active to App');
      onSessionStatusChange?.('active');

      // Reset completion flag when session starts
      if (gameState.session && sessionCompletedRef.current) {
        sessionCompletedRef.current = false;
        lastCompletedSessionIdRef.current = null;
      }
    }

    // 🔧 REMOVED: Bidirectional sync logic that caused loops
    // Old code: if (isSessionActiveFromApp !== isSessionActive) { onSessionActiveChange?.(isSessionActive); }

  }, [
    // 🔧 TASK 2.3: Minimal dependencies - no bidirectional sync dependencies
    gameState.session?.endTime,
    gameState.isActive,
    gameState.session?.id,
    isSessionActive,
    sessionStatus, // App's session status (source of truth)
    // 🔧 REMOVED: isSessionActiveFromApp (bidirectional sync)
    onSessionStatusChange,
    // 🔧 REMOVED: onSessionActiveChange (bidirectional sync)
    onSessionComplete,
    stats,
    originalSessionContext
  ]); // 🔧 SPRINT 2: Unidirectional dependencies - no circular references

  /**
   * 🎆 SPRINT 2 COMPLETE: All session handling infinite loops eliminated!
   * ✅ Task 2.1: Fixed App.tsx session completion handler dependencies
   * ✅ Task 2.2: Consolidated GameScreen overlapping useEffect hooks
   * ✅ Result: No more cascading state updates or duplicate completion calls
   */

  /**
   * SPRINT 1: Handle manual configuration changes (only when session is NOT active)
   * Optimized to prevent infinite loops
   */
  useEffect(() => {
    // Only log when not in an active session to prevent render loops
    if (!isSessionActive) {
      console.log('⚡ Configuration change detected:', {
        mode: trainingMode,
        dayNumber,
        roundNumber,
        handMode,
        complexityMode,
        customFingers
      });
      console.log('✅ Configuration updated for next session');
    }
  }, [trainingMode, dayNumber, roundNumber, handMode, complexityMode, customFingers, isSessionActive]);

  /**
   * End current session
   */
  // const handleEndSession = () => {
  //   endSession();
  //   onSessionStatusChange?.('completed');
  // };

  /**
   * Restart session
   */
  const handleRestartSession = () => {
    endSession();
    setTimeout(() => {
      handleStartSession();
    }, 100);
  };

  return (
    <FeedbackSystem>
      <div className={`game-screen ${className}`}>

        {/* 🎯 UNIFIED: Single layout for both Course and Custom modes */}
        <div className="game-main unified-layout">
          {/* Main Content Area - Training Widgets */}
          <div className="training-widgets-area">
            {/* Main Training Widget - ALWAYS VISIBLE */}
            <div className="main-training-widget">
              {trainingMode === 'course' ? (
                // Course mode: Show course widget always
                <UnifiedCourseWidget
                  currentLevel={levelNumber || 1}
                  currentDay={dayNumber || 1}
                  currentRound={roundNumber || 1}
                  courseProgress={courseProgress || []}
                  fingerConfig={fingerConfig}
                  description={description}
                  onDaySelect={onCourseDaySelect || (() => { })}
                  onRoundSelect={onCourseRoundSelect || (() => { })}
                  onStartTraining={handleStartSession}
                  // 🎯 FINAL CLEANUP: Pass mode switching props
                  onModeChange={onModeChange}
                  currentMode={trainingMode}
                  onRequestLogin={onRequestLogin}
                  isAuthenticated={isAuthenticated}
                />
              ) : trainingMode === 'oneWord' ? (
                <UnifiedCourseWidget
                  currentLevel={1}
                  currentDay={1}
                  currentRound={1}
                  courseProgress={[]}
                  fingerConfig={fingerConfig}
                  description={description}
                  onDaySelect={() => { }}
                  onRoundSelect={() => { }}
                  onStartTraining={handleStartSession}
                  customMode={true}
                  customStats={stats}
                  currentMode={trainingMode}
                  onModeChange={onModeChange}
                  oneWordValue={oneWordText}
                  onOneWordChange={setOneWordText}
                />
              ) : (
                // Custom mode: Show unified course widget style for consistency
                <UnifiedCourseWidget
                  currentLevel={1}
                  currentDay={1}
                  currentRound={1}
                  courseProgress={[]}
                  fingerConfig={fingerConfig}
                  description={description}
                  onDaySelect={() => { }}
                  onRoundSelect={() => { }}
                  onStartTraining={handleStartSession}
                  customMode={true}
                  customStats={stats} // Pass current session stats
                  // 🎯 FINAL CLEANUP: Pass mode switching and complexity props
                  onModeChange={onModeChange}
                  currentMode={trainingMode}
                  complexityMode={complexityMode}
                  onComplexityChange={onComplexityChange}
                />
              )}
            </div>

            {/* Completion widgets - ONLY when session completed - TO THE RIGHT */}
            {sessionStatus === 'completed' && (
              trainingMode === 'oneWord' && oneWordText.trim() ? (
                // One Word: race-complete summary + training statistics (replaces dots)
                <div className="one-word-complete-container">
                  <OneWordRaceComplete
                    uid={uid}
                    word={oneWordText.trim()}
                    race={{
                      bestLapMs: oneWordLap.bestLapMs,
                      bestStreak: oneWordLap.bestStreak,
                      lapsCompleted: oneWordLap.lapsCompleted,
                    }}
                    record={oneWordRecord}
                    onNewRound={handleRestartSession}
                  />
                  <OneWordTrainingStats uid={uid} word={oneWordText.trim()} />
                </div>
              ) : (
                <div className="progress-widget-container">
                  <ProgressVisualization
                    progressTiles={progressTiles}
                    className="completed"
                    sessionStats={stats}
                    onNewRound={handleRestartSession}
                  />
                </div>
              )
            )}

            {/* 🎯 PHASE 2: Hand Picker Controls - GOVERNING in Custom, DEMONSTRATION in Course */}
            {sessionStatus !== 'active' && (
              <div className="hand-picker-preview-container">
                <LeftHandPicker
                  selectedFingers={trainingMode === 'custom' ? customFingers || [] : []}
                  externalFingerData={trainingMode !== 'custom' ? fingerConfig.activeFingersLeft as any[] : []}
                  mode={trainingMode !== 'custom' ? 'demonstration' : 'active'}
                  onChange={(fingers) => {
                    if (trainingMode === 'custom') {
                      console.log('🎯 PHASE 2: Left hand picker GOVERNING mode:', fingers);
                      if (onFingerSelectionChange) {
                        const rightFingers = (customFingers || []).filter(f => f.startsWith('right'));
                        const newSelectedFingers = [...fingers.filter(f => f.startsWith('left')), ...rightFingers];
                        onFingerSelectionChange(newSelectedFingers);
                      }
                    } else {
                      console.log('🎯 PHASE 2: Left hand picker DEMONSTRATION mode:', fingers);
                    }
                  }}
                  disabled={trainingMode !== 'custom'}
                  className="hand-picker-left"
                />

                {/* 🆕 NEW: Central Information Display */}
                <div className="central-finger-info">

                  {/* Left Hand Info */}
                  <div className="hand-info-section">
                    <h3 className="hand-info-title">Left Hand</h3>
                    <div className="finger-info-list">
                      {fingerConfig.activeFingersLeft.length > 0 ? (
                        fingerConfig.activeFingersLeft.map((finger) => {
                          const leftFinger = finger as LeftHandFinger;
                          const fingerName = getFingerDisplayName(leftFinger);
                          const keys = FINGER_KEY_MAP[leftFinger] || [];
                          return (
                            <div key={finger} className="finger-info-item">
                              <span className="finger-name">{fingerName}:</span>
                              <span className="finger-keys">{keys.join(' ')}</span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-fingers-message">No fingers selected</div>
                      )}
                    </div>
                  </div>

                  {/* Right Hand Info */}
                  <div className="hand-info-section">
                    <h3 className="hand-info-title">Right Hand</h3>
                    <div className="finger-info-list">
                      {fingerConfig.activeFingersRight.length > 0 ? (
                        fingerConfig.activeFingersRight.map((finger) => {
                          const rightFinger = finger as RightHandFinger;
                          const fingerName = getFingerDisplayName(rightFinger);
                          const keys = RIGHT_FINGER_KEY_MAP[rightFinger] || [];
                          return (
                            <div key={finger} className="finger-info-item">
                              <span className="finger-name">{fingerName}:</span>
                              <span className="finger-keys">{keys.join(' ')}</span>
                            </div>
                          );
                        })
                      ) : (
                        <div className="no-fingers-message">No fingers selected</div>
                      )}
                    </div>
                  </div>
                </div>

                <RightHandPicker
                  selectedFingers={trainingMode === 'custom' ? customFingers || [] : []}
                  externalFingerData={trainingMode !== 'custom' ? fingerConfig.activeFingersRight as any[] : []}
                  mode={trainingMode !== 'custom' ? 'demonstration' : 'active'}
                  onChange={(fingers) => {
                    if (trainingMode === 'custom') {
                      console.log('🎯 PHASE 2: Right hand picker GOVERNING mode:', fingers);
                      if (onFingerSelectionChange) {
                        const leftFingers = (customFingers || []).filter(f => f.startsWith('left'));
                        const newSelectedFingers = [...leftFingers, ...fingers.filter(f => f.startsWith('right'))];
                        onFingerSelectionChange(newSelectedFingers);
                      }
                    } else {
                      console.log('🎯 PHASE 2: Right hand picker DEMONSTRATION mode:', fingers);
                    }
                  }}
                  disabled={trainingMode !== 'custom'}
                  className="hand-picker-right"
                />
              </div>
            )}
          </div>

          {/* 🎯 UNIFIED: Full-screen immersive training overlay for BOTH modes */}
          {sessionStatus === 'active' && (
            <div className="training-overlay">
              {/* Finger Hints during active session */}
              {showFingerHints && (
                <div className="finger-hints-container">
                  <FingerHints
                    currentFinger={currentFinger}
                    nextFinger={nextFinger}
                    isVisible={showFingerHints}
                    isCalibrating={isCalibrating}
                    onCalibrationChange={setIsCalibrating}
                    className="minimal"
                  />
                </div>
              )}

              {trainingMode === 'oneWord' && oneWordText.trim() && (
                <div className="one-word-hud-container">
                  <OneWordMetricsHUD
                    word={oneWordText.trim()}
                    bestLapMs={oneWordLap.bestLapMs}
                    currentStreak={oneWordLap.currentStreak}
                    bestStreak={oneWordLap.bestStreak}
                    lapsCompleted={oneWordLap.lapsCompleted}
                    record={oneWordRecord}
                    letterMistakes={letterMistakes}
                  />
                </div>
              )}

              {/* Letter Display during active session */}
              <div className="letter-display-container">
                <LetterDisplay
                  currentSequence={currentSequence}
                  currentSequenceIndex={currentSequenceIndex}
                  nextSequence={nextSequence}
                />
              </div>
            </div>
          )}

          {/* Ready State Messages - Removed for unified widget approach */}

          {/* Completion State - Now handled in merged ProgressVisualization widget */}

          {/* Pause State */}
          {!isSessionActive && sessionStatus === 'active' && (
            <div className="status-message paused">
              <h2>⏸️ Session Paused</h2>
              <p>Press "Resume" to continue training</p>
            </div>
          )}
        </div>

        {/* Footer Instructions */}
        <div className="game-footer">
          {sessionStatus === 'active' && isSessionActive && (
            <div className="instructions">
              <p>💡 Type the letter shown. No backspace needed - just type the correct letter when you make a mistake!</p>
            </div>
          )}

          {isCalibrating && (
            <div className="calibration-notice">
              <p>🎯 Calibration Mode: Position the finger circles over your finger tips for accurate hints</p>
            </div>
          )}

          {/* 🔥 SIMPLIFIED: Simple message for empty word combinations */}
          {sessionStatus === 'active' && utilTrainingMode.complexity === 'words' && currentSequence && currentSequence.length === 0 && (
            <div className="empty-words-message">
              <div className="message-content">
                <h3>ℹ️ No Real Words Available</h3>
                <p>This finger combination only has consonant letters, so no English words can be formed.</p>
                <p><strong>What you can do:</strong></p>
                <ul>
                  <li>Switch to letter training mode (complexity 1-5)</li>
                  <li>Choose different fingers that include vowels</li>
                  <li>Try adding fingers like A, E, I, O, U keys</li>
                </ul>
                <button
                  className="switch-to-letters-btn"
                  onClick={() => {
                    // This would ideally trigger a complexity change to letter mode
                    console.log('User wants to switch to letter mode');
                  }}
                >
                  Switch to Letter Training
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Training Timer - Only show during active session */}
        <TrainingTimer
          startTime={gameState.session?.startTime || Date.now()}
          isActive={sessionStatus === 'active' && isSessionActive}
          timeRemaining={timeRemaining}
        />

        <style>{`
        .game-screen {
          display: flex;
          flex-direction: column;
          height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #ffffff;
          overflow: hidden;
        }

        /* 🎯 CORRECTED: Left-aligned layout - training widget replaces left sidebar */
        .game-main.unified-layout {
          display: flex;
          flex: 1;
          gap: 8px;
          padding: 20px;
          overflow: hidden;
          align-items: flex-start;
          justify-content: flex-start; /* Left align like the old sidebar */
          position: relative;
        }

        .training-widgets-area {
          display: flex;
          gap: 4px; /* Reduced gap for compact layout */
          align-items: flex-start;
          justify-content: flex-start; /* Left align */
        }

        .main-training-widget {
          flex: 0 0 300px; /* Fixed width like original sidebar */
          width: 300px;
          height: 572px;
        }

        .progress-widget-container {
          flex: 0 0 300px; /* Same width as main widget */
          width: 300px;
        }

        /* One Word completion: keep the SAME 300px footprint as the old single
           widget (so the hand pickers / keyboard stay put). The race-complete and
           training-statistics panels stack vertically and scroll internally. */
        .one-word-complete-container {
          flex: 0 0 300px;
          width: 300px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 600px;
          overflow-y: auto;
        }

        /* Old widget styles removed - now using unified approach */

        /* Old course controls removed - now using unified completion overlay */

        /* Unified sidebar styles - specific classes handled above */

        /* Unified layout removes old game-center approach */

        /* 🔧 CRITICAL FIX: Training overlay doesn't interfere with sidebar positioning */
        .training-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 100;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 20px;
          pointer-events: none; /* Allow interaction with background elements */
        }

        .training-overlay > * {
          pointer-events: auto; /* Re-enable interaction for child elements */
        }

        .finger-hints-container {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          z-index: 10;
        }

        .letter-display-container {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          max-width: 600px;
          width: 100%;
          position: relative;
          z-index: 5;
        }

        .one-word-hud-container {
          position: absolute;
          top: 96px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          justify-content: center;
          padding: 6px 0;
          z-index: 6;
          pointer-events: auto;
        }

        .status-message {
          text-align: center;
          padding: 40px;
          background: rgba(17, 24, 39, 0.95);
          border-radius: 16px;
          border: 1px solid rgba(75, 85, 99, 0.3);
          backdrop-filter: blur(20px);
          max-width: 500px;
        }

        .status-message.paused {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 200;
        }

        .status-message h2 {
          margin: 0 0 16px 0;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .status-message p {
          margin: 8px 0;
          color: #d1d5db;
          line-height: 1.5;
        }

        .status-message.ready h2 {
          color: #3b82f6;
        }

        .status-message.completed h2 {
          color: #22c55e;
        }

        .status-message.paused h2 {
          color: #f59e0b;
        }

        /* Completion stats now handled in merged ProgressVisualization widget */

        .game-footer {
          padding: 12px 24px;
          background: rgba(15, 23, 42, 0.8);
          border-top: 1px solid rgba(75, 85, 99, 0.3);
          text-align: center;
        }

        .instructions, .calibration-notice {
          margin: 0;
          font-size: 0.875rem;
          color: #9ca3af;
        }

        .calibration-notice {
          color: #fbbf24;
        }
        
        /* 🔥 SIMPLIFIED: Empty Words Message */
        .empty-words-message {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 300;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .empty-words-message .message-content {
          background: rgba(17, 24, 39, 0.95);
          border-radius: 16px;
          border: 2px solid rgba(59, 130, 246, 0.5);
          padding: 32px;
          max-width: 500px;
          text-align: left;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
        }
        
        .empty-words-message h3 {
          margin: 0 0 16px 0;
          color: #3b82f6;
          font-size: 1.5rem;
          font-weight: 700;
          text-align: center;
        }
        
        .empty-words-message p {
          margin: 12px 0;
          color: #d1d5db;
          line-height: 1.6;
        }
        
        .empty-words-message ul {
          margin: 16px 0;
          padding-left: 20px;
          color: #d1d5db;
        }
        
        .empty-words-message li {
          margin: 8px 0;
          line-height: 1.4;
        }
        
        .switch-to-letters-btn {
          margin-top: 20px;
          padding: 12px 24px;
          background: linear-gradient(45deg, #3b82f6, #2563eb);
          border: none;
          border-radius: 8px;
          color: white;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          width: 100%;
          text-align: center;
        }
        
        .switch-to-letters-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }

        /* 🔧 DEBUGGING: Visual debugging borders (enable when testing) */
        /*
        .sidebar.stats-sidebar {
          border: 2px solid red !important;
        }
        .training-widgets-area {
          border: 2px solid blue !important;
        }
        .main-training-widget {
          border: 2px solid yellow !important;
        }
        .progress-widget-container {
          border: 2px solid purple !important;
        }
        */

        /* 🎯 PHASE 1: Hand Picker Preview Styling - Natural Embedding */
        .hand-picker-preview-container {
          flex: 0 0 auto;
          display: flex;
          gap: 0px; /* No gap - central info provides spacing */
          align-items: flex-start;
          /* Natural embedding - no background, no border */
        }

        .hand-picker-left,
        .hand-picker-right {
          /* Hand pickers are 507x580px at 100% scale - EXACT sizing */
          width: 507px !important;
          height: 580px !important;
          flex-shrink: 0;
          flex-grow: 0;
          min-width: 507px;
          max-width: 507px;
          min-height: 580px;
          max-height: 580px;
        }

        /* 🆕 NEW: Central Information Display Styling */
        .central-finger-info {
          flex: 0 0 200px;
          width: 200px;
          margin-top: 20px; /* Moved higher - align with top edge */
          display: flex;
          flex-direction: column;
          gap: 32px;
          padding: 0 16px;
        }

        .hand-info-section {
          text-align: center;
        }

        .hand-info-title {
          font-size: 1.25rem;
          font-weight: bold;
          color: #ffffff;
          margin-bottom: 12px;
          border-bottom: 1px solid rgba(156, 163, 175, 0.3);
          padding-bottom: 8px;
        }

        .finger-info-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .finger-info-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 2px;
        }

        .finger-name {
          font-weight: 600;
          color: #ffffff;
          font-size: 0.9rem;
        }

        .finger-keys {
          color: #d1d5db;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          letter-spacing: 0.5px;
        }

        .no-fingers-message {
          color: #9ca3af;
          font-style: italic;
          font-size: 0.85rem;
          text-align: center;
        }

        /* Responsive design */
        @media (max-width: 1400px) {
          .central-finger-info {
            flex: 0 0 160px;
            width: 160px;
            padding: 0 8px;
          }

          .hand-info-title {
            font-size: 1.1rem;
          }

          .finger-name {
            font-size: 0.8rem;
          }

          .finger-keys {
            font-size: 0.75rem;
          }
        }

        @media (max-width: 1200px) {
          .main-training-widget,
          .progress-widget-container {
            flex: 0 0 250px;
            width: 250px;
          }
          
          .central-finger-info {
            display: none; /* Hide on smaller screens */
          }
        }

        @media (max-width: 768px) {
          .game-main {
            flex-direction: column;
            gap: 16px;
            padding: 16px;
          }

          .training-widgets-area {
            flex-direction: column;
            width: 100%;
          }

          .main-training-widget,
          .progress-widget-container {
            flex: 0 0 auto;
            width: 100%;
            align-items: center !important;
            max-height: none;
          }

          .main-training-widget {
            height: auto;
          }

          .finger-hints-container {
            position: relative;
            top: auto;
            left: auto;
            transform: none;
          }

          .hand-picker-preview-container {
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .central-finger-info {
            display: block;
            width: 100%;
            margin-top: 0;
            flex-direction: row;
            justify-content: space-around;
            padding: 16px;
            background: rgba(17, 24, 39, 0.8);
            border-radius: 8px;
            border: 1px solid rgba(75, 85, 99, 0.3);
          }

          .hand-info-section {
            flex: 1;
          }
        }

        /* Focus and game state styles */
        .game-screen.active {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }

        .game-screen.paused {
          filter: brightness(0.7);
        }

        .game-screen.completed {
          background: linear-gradient(135deg, #0f172a 0%, #1e3a3a 100%);
        }
      `}</style>
      </div>
    </FeedbackSystem>
  );
});

export default GameScreen;